import { Injectable } from "@angular/core";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service";
import { AIGenerationData, FILTER_MODEL_KEY, Linqset, LINQSET_TYPE } from "../common/linqset-model";
import { createInsightsFilter, ModelSummary } from "../common/organisation";
import { AIEpmChange, AIEpmDecision, AIEpmDecisionsResponse, AIEpmFutureStateResponse, AIEpmValueSuggestion, AIEpmValueSuggestionsResponse, EpmDecisionEvidence, EpmFutureStateRequest } from "../common/epm-decisions";
import { lastValueFrom } from "rxjs";
import { ModelChangeRequest, ModelChangeResponse } from "../common/model-changes";
import { RecommendationRequest, RecommendationsResponse } from "../common/process-recommendations";

@Injectable({
    providedIn: 'root',
})
export class AIServices { 
    constructor(private repoService:RepositoryService) {
    }

    async getTokens(organisationKey:string) {
        return await this.repoService.callSingleAPI(this.repoService.getData(ORGANISATION_API(organisationKey) +  '/ai/gettokens'), "AI Services", "Request ai token")
    }

    async getPrompts(organisationKey:string) {
        return await this.repoService.callSingleAPI(this.repoService.getData(ORGANISATION_API(organisationKey) +  '/ai/prompts'), "AI Services", "Get AI prompts")
    }

    // generates EPM scorecard decisions from the evidence the scorecard read from the model. Errors are left to the
    // caller, which falls back to rule-based decisions rather than showing an error dialog
    async generateEpmDecisions(organisationKey:string, workspaceKey:string, evidence:EpmDecisionEvidence):Promise<AIEpmDecision[]> {
        const response = await lastValueFrom(this.repoService.create(ORGANISATION_API(organisationKey, workspaceKey) + '/ai/epm/decisions', {evidence}, 180000)) as AIEpmDecisionsResponse
        return response?.decisions || []
    }

    // suggested values per item for the outputs in the scorecard's evidence, with AI's reasoning. Errors are left to the caller
    async suggestEpmValues(organisationKey:string, workspaceKey:string, evidence:EpmDecisionEvidence):Promise<AIEpmValueSuggestion[]> {
        const response = await lastValueFrom(this.repoService.create(ORGANISATION_API(organisationKey, workspaceKey) + '/ai/epm/values', {evidence}, 180000)) as AIEpmValueSuggestionsResponse
        return response?.suggestions || []
    }

    // the changes to the model that carry out the chosen EPM decisions, for a future state. Errors are left to the caller
    async generateEpmFutureState(organisationKey:string, workspaceKey:string, request:EpmFutureStateRequest):Promise<AIEpmChange[]> {
        const response = await lastValueFrom(this.repoService.create(ORGANISATION_API(organisationKey, workspaceKey) + '/ai/epm/futurestate', request, 180000)) as AIEpmFutureStateResponse
        return response?.changes || []
    }

    // the changes a description makes to a project's models, or AI's questions about it. Errors are left to the caller
    async generateModelChanges(organisationKey:string, projectKey:string, request:ModelChangeRequest):Promise<ModelChangeResponse> {
        return await lastValueFrom(this.repoService.create(ORGANISATION_API(organisationKey, projectKey) + '/ai/changes', request, 180000)) as ModelChangeResponse
    }

    // overall recommendations from a workspace's economics and its simulation. Errors are left to the caller
    async generateRecommendations(organisationKey:string, workspaceKey:string, request:RecommendationRequest):Promise<RecommendationsResponse> {
        return await lastValueFrom(this.repoService.create(ORGANISATION_API(organisationKey, workspaceKey) + '/ai/recommendations', request, 180000)) as RecommendationsResponse
    }

    async groupModel(linqset:Linqset) {
        return await this.repoService.callSingleAPI(this.repoService.getData(MODEL_API(linqset.organisationKey, linqset.modelKey, linqset.workspaceKey) + '/ai/groups'), "AI services", "group model")        
    }

    async getModelSummaries(linqset:Linqset) {
        if (linqset.type === LINQSET_TYPE.MODEL || linqset.type === LINQSET_TYPE.ORGANISATION) 
            return await this.repoService.callSingleAPI(this.repoService.getData(MODEL_API(linqset.organisationKey, linqset.modelKey, linqset.workspaceKey) + '/ai/summaries'), "AI services", "Get model summaries")
        else if (linqset.filter?._key)
            return await this.repoService.callSingleAPI(this.repoService.getData(ORGANISATION_API(linqset.organisationKey, linqset.workspaceKey) + `/filters/${linqset.filter._key}/ai/summaries`), "AI services", "Get model summaries")

        return []
    }

    async generateModelSummary(linqset:Linqset, prompt:string) {
        if (!linqset.modelKey||linqset.modelKey===FILTER_MODEL_KEY)
            return await this.repoService.callSingleAPI(this.repoService.create(ORGANISATION_API(linqset.organisationKey, linqset.workspaceKey) + '/ai/summaries', {filter:linqset.filter, prompt}), "AI services", "Generate AI model summary")
        else return await this.repoService.callSingleAPI(this.repoService.create(MODEL_API(linqset.organisationKey, linqset.modelKey, linqset.workspaceKey) + '/ai/summaries', {filter:linqset.filter, prompt}), "AI services", "Generate AI model summary")
        
    }

    async saveModelSummary(linqset:Linqset, summary:ModelSummary) {
        if (linqset.filter?._key)
            return await this.repoService.callSingleAPI(this.repoService.update(ORGANISATION_API(linqset.organisationKey, linqset.workspaceKey) + `/filters/${linqset.filter._key}/ai/summaries/${summary._key}`, summary), "AI services", "Save AI model summary")
        else if (linqset.type === LINQSET_TYPE.MODEL || linqset.type === LINQSET_TYPE.ORGANISATION) 
            return await this.repoService.callSingleAPI(this.repoService.update(MODEL_API(linqset.organisationKey, linqset.modelKey, linqset.workspaceKey) + `/ai/summaries/${summary._key}`, summary), "AI services", "Save AI model summary")
    }

    async deleteModelSummary(linqset:Linqset, summary:ModelSummary) {
        if (linqset.type === LINQSET_TYPE.MODEL || linqset.type === LINQSET_TYPE.ORGANISATION) 
            return await this.repoService.callSingleAPI(this.repoService.delete(MODEL_API(linqset.organisationKey, linqset.modelKey, linqset.workspaceKey) + `/ai/summaries/${summary._key}`), "AI services", "Delete AI model summary")
        else if (linqset.filter?._key)
            return await this.repoService.callSingleAPI(this.repoService.delete(ORGANISATION_API(linqset.organisationKey, linqset.workspaceKey) + `/filters/${linqset.filter._key}/ai/summaries/${summary._key}`), "AI services", "Delete AI model summary")

    }

    // the first step of generating a model, on its own: AI's description of the process, for the user to check before
    // it is built. showError false leaves the caller to show a problem
    async describeProcess(organisationKey:string, modelKey:string, workspaceKey:string, aiData:AIGenerationData, file?:File, showError:boolean=true):Promise<string> {
        const api = MODEL_API(organisationKey, modelKey, workspaceKey)
        if (file) {
            const formData = new FormData();
            formData.append("aiFile", file);
            formData.append("aiData", JSON.stringify(aiData))
            const result = await this.repoService.callSingleAPI(this.repoService.postFile(api + '/ai/describefile', formData, 300000), "AI services", "Describe process using file", undefined, undefined, undefined, undefined, showError)
            return result.body?.description
        }
        const result = await this.repoService.callSingleAPI(this.repoService.create(api + '/ai/describe', aiData, 300000), "AI services", "Describe process", undefined, undefined, undefined, undefined, showError)
        return result?.description
    }

    async generateModel(organisationKey:string, modelKey:string, workspaceKey:string, aiData:AIGenerationData, save:boolean, file?:File, showError:boolean=true) {
        if (file) {
            const formData = new FormData();
            formData.append("aiFile", file);
            formData.append("aiData", JSON.stringify(aiData))
            const result = await this.repoService.callSingleAPI(this.repoService.postFile(MODEL_API(organisationKey, modelKey, workspaceKey) + '/ai/uploadfile' + (save?'?save=true':''), formData, 300000), "Modelling AI component", "Create model using file", undefined, undefined, undefined, undefined, showError)
            return result.body
        }else {      
            return await this.repoService.callSingleAPI(this.repoService.create(MODEL_API(organisationKey, modelKey, workspaceKey) + '/ai/generatemodel' + (save?'?save=true':''), aiData, 300000), "AI services", "Create model using transcript", undefined, undefined, undefined, undefined, showError)
        }
    }
}